import React from "react";
import { Box, Divider, Paper, Stack, Typography, alpha } from "@mui/material";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { formatAmount } from "../../../../utils/hooks/dataHandler";

export const sharedBasedPaymentLegends = [
  {
    label: "I",
    title: "Share cost / company payment",
    description:
      "Cost of shares from the linked Section 1 transfer, or the manual amount paid or recharged to the company.",
  },
  {
    label: "II",
    title: "SPV market cost",
    description:
      "Cost to the SPV in acquiring the transferred shares from the open market.",
  },
  {
    label: "III",
    title: "Treasury share cost",
    description:
      "Cost incurred by the company or holding company in acquiring the treasury shares.",
  },
  {
    label: "IV",
    title: "Market or NAV cap",
    description:
      "Open-market price at issue or transfer to employees, or net asset value if that price cannot be determined.",
  },
];

const FurtherDeductionOutput = ({ output }) => (
  <Paper
    component='section'
    aria-label='Further Deduction Output'
    elevation={0}
    sx={{
      p: { xs: 3, sm: 4 },
      bgcolor: "background.paper",
      borderRadius: 3,
      border: "1px solid",
      borderColor: "divider",
      boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.05)",
      transition: "box-shadow 0.2s ease-in-out",
      "&:hover": {
        boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.08)",
      },
    }}
  >
    <Stack direction='row' alignItems='center' spacing={2} sx={{ mb: 3 }}>
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: 2,
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
        }}
      >
        <SummarizeIcon color='primary' />
      </Box>
      <Typography
        component='h2'
        variant='h5'
        fontWeight={600}
        color='text.primary'
      >
        Final Qualifying Deduction Output
      </Typography>
    </Stack>

    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={2}
      alignItems={{ xs: "flex-start", sm: "center" }}
      justifyContent='space-between'
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        border: "1px solid",
        borderColor: (theme) => alpha(theme.palette.primary.main, 0.1),
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant='subtitle1' fontWeight={600}>
          Total Qualifying Deduction
        </Typography>
      </Box>
      <Typography
        component='p'
        variant='h4'
        color='primary.main'
        fontWeight={600}
        sx={{
          overflowWrap: "anywhere",
          textAlign: { xs: "left", sm: "right" },
        }}
      >
        <Box
          component='span'
          sx={{ mr: 1, fontSize: "0.875rem", color: "text.secondary" }}
        >
          SGD
        </Box>
        {formatAmount(output?.qualifyingDeductionSgd ?? 0)}
      </Typography>
    </Stack>

    <Divider sx={{ mt: 3, mb: 2 }} />

    <Box sx={{ color: "text.secondary" }}>
      <Typography component='h3' variant='caption' fontWeight={600}>
        I-IV legend
      </Typography>
      <Box
        component='dl'
        sx={{
          mt: 0.75,
          mb: 0,
          display: "grid",
          gridTemplateColumns: "auto minmax(0, 1fr)",
          columnGap: 1.5,
          rowGap: 1,
        }}
      >
        {sharedBasedPaymentLegends.map((legend) => (
          <React.Fragment key={legend.label}>
            <Typography
              component='dt'
              variant='caption'
              fontWeight={600}
              sx={{ lineHeight: 1.6 }}
            >
              {legend.label}
            </Typography>
            <Typography
              component='dd'
              variant='caption'
              sx={{ m: 0, lineHeight: 1.6 }}
            >
              <Box component='strong' sx={{ fontWeight: 600 }}>
                {legend.title}
              </Box>
              {" — "}
              {legend.description}
            </Typography>
          </React.Fragment>
        ))}
      </Box>
    </Box>
  </Paper>
);

export default FurtherDeductionOutput;
